import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/services/api";
import type { Assignment, AssignmentMappingResponse, ScheduleWindowInput, Topic } from "@/types";

/**
 * React Query hooks for the admin Assignments feature. These wrap the existing
 * API client methods — the client is unchanged — and own the query keys,
 * caching, and invalidation-on-success that previously lived as manual
 * `useEffect` loaders + manual refetch in the page and its modals.
 *
 * The user-assignment mappings list is keyed by programme + week/assignment
 * filters, so changing a filter changes the query key and React Query refetches
 * naturally (replacing the suppressed "reload on programme/filter change"
 * effect). Follows the Sources slice template.
 */

export interface UserAssignmentsParams {
  programmeId: string;
  weekNumber?: number;
  assignmentNumber?: number;
}

export const assignmentKeys = {
  all: ["admin", "assignments"] as const,
  topics: () => [...assignmentKeys.all, "topics"] as const,
  mappings: (params: UserAssignmentsParams) => [...assignmentKeys.all, "mappings", params] as const,
  byProgramme: (programmeId: string) =>
    [...assignmentKeys.all, "byProgramme", programmeId] as const,
  autoAssignPreview: (assignmentId: string) =>
    [...assignmentKeys.all, "autoAssignPreview", assignmentId] as const,
};

/** Topics for the assign-topic picker. */
export function useAssignmentTopics() {
  return useQuery<Topic[]>({
    queryKey: assignmentKeys.topics(),
    queryFn: async () => {
      const data = await api.getTopics();
      return data.items;
    },
  });
}

/** User-assignment mappings for a programme, optionally filtered by week/assignment. */
export function useUserAssignments(params: UserAssignmentsParams | null) {
  return useQuery<AssignmentMappingResponse[]>({
    queryKey: assignmentKeys.mappings(params ?? { programmeId: "" }),
    queryFn: () =>
      api.getUserAssignments({
        programmeId: (params as UserAssignmentsParams).programmeId,
        weekNumber: params?.weekNumber,
        assignmentNumber: params?.assignmentNumber,
      }),
    enabled: Boolean(params?.programmeId),
  });
}

/** Assignments for a programme (used by the per-user AssignmentListModal). */
export function useAssignmentsByProgramme(programmeId: string | null, enabled = true) {
  return useQuery<Assignment[]>({
    queryKey: assignmentKeys.byProgramme(programmeId ?? ""),
    queryFn: () => api.getAssignmentsByProgramme(programmeId as string),
    enabled: Boolean(programmeId) && enabled,
  });
}

/** Auto-assign preview for a window. */
export function useAutoAssignPreview(assignmentId: string | null, enabled = true) {
  return useQuery({
    queryKey: assignmentKeys.autoAssignPreview(assignmentId ?? ""),
    queryFn: () => api.previewAutoAssign(assignmentId as string),
    enabled: Boolean(assignmentId) && enabled,
  });
}

/** Invalidate every Assignments query (mappings, lists, previews) after a write. */
function useInvalidateAssignments() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: assignmentKeys.all });
}

export function useCreateUserAssignmentsBulk() {
  const invalidate = useInvalidateAssignments();
  return useMutation({
    mutationFn: (assignmentId: string) => api.createUserAssignmentsBulk(assignmentId),
    onSuccess: invalidate,
  });
}

export function useUpdateUserAssignment() {
  const invalidate = useInvalidateAssignments();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: { topic_id?: string; status?: string } }) =>
      api.updateUserAssignment(id, data),
    onSuccess: invalidate,
  });
}

export function useAssignTopicToUser() {
  const invalidate = useInvalidateAssignments();
  return useMutation({
    mutationFn: ({
      userProgrammeId,
      assignmentId,
      topicId,
    }: {
      userProgrammeId: string;
      assignmentId: string;
      topicId: string;
    }) => api.assignTopicToUser(userProgrammeId, assignmentId, topicId),
    onSuccess: invalidate,
  });
}

export function useAutoAssignTopics() {
  const invalidate = useInvalidateAssignments();
  return useMutation({
    mutationFn: ({ assignmentId, topicAreaId }: { assignmentId: string; topicAreaId?: string }) =>
      api.autoAssignTopics(assignmentId, topicAreaId),
    onSuccess: invalidate,
  });
}

export function useDeleteSubmission() {
  const invalidate = useInvalidateAssignments();
  return useMutation({
    mutationFn: (submissionId: string) => api.deleteSubmission(submissionId),
    onSuccess: invalidate,
  });
}

export function useCreateAssignment() {
  const invalidate = useInvalidateAssignments();
  return useMutation({
    // week_number / assignment_number are server-derived (KC-374).
    mutationFn: (data: { programme_id: string; start_date: string; deadline: string }) =>
      api.createAssignment(data),
    onSuccess: invalidate,
  });
}

export function useUpdateAssignment() {
  const invalidate = useInvalidateAssignments();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Partial<{ start_date: string; deadline: string }>;
    }) => api.updateAssignment(id, data),
    onSuccess: invalidate,
  });
}

export function useDeleteAssignment() {
  const invalidate = useInvalidateAssignments();
  return useMutation({
    mutationFn: (assignmentId: string) => api.deleteAssignment(assignmentId),
    onSuccess: invalidate,
  });
}

/** Apply a full cohort schedule (add/remove/re-date windows) via the bulk endpoint. */
export function useApplySchedule() {
  const invalidate = useInvalidateAssignments();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      programmeId,
      windows,
      confirm,
    }: {
      programmeId: string;
      windows: ScheduleWindowInput[];
      confirm?: string;
    }) => api.applySchedule(programmeId, windows, confirm),
    onSuccess: () => {
      invalidate();
      queryClient.invalidateQueries({ queryKey: ["admin", "programmes"] });
    },
  });
}
